import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_PORTFOLIO_SUMMARY } from '../graphql/queries/portfolioSummary';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { usePositionContext } from '../contexts/PositionContext';
import { useImportModal } from '../contexts/ImportModalContext';
import PortfolioRequired from '../components/PortfolioRequired';
import PortfolioMetrics from '../components/PortfolioMetrics';
import PortfolioBreakdownBar from '../components/PortfolioBreakdownBar';
import PlanningStatus from '../components/PlanningStatus';
import TabbedCharts from '../components/TabbedCharts';
import MarketCapBreakdown from '../components/MarketCapBreakdown';
import GroupBreakdown from '../components/GroupBreakdown';
import LocationBreakdown from '../components/LocationBreakdown';
import InvestmentGoalsModal from '../components/InvestmentGoalsModal';
import PortfolioManagementModal from '../components/PortfolioManagementModal';
import { parseFloatFromString, subtractDecimalStrings } from '../utils/formatters';
import EmptyState from '../components/EmptyState';

const DashboardPage = () => {
  const { currentPortfolioId, currentPortfolio } = usePortfolioContext();
  const { positions } = usePositionContext();
  const { openImportModal } = useImportModal();

  const [isGoalsOpen, setIsGoalsOpen] = useState(false);
  const [showPortfolioManager, setShowPortfolioManager] = useState(false);

  const { loading, error, data } = useQuery(GET_PORTFOLIO_SUMMARY, {
    variables: { portfolioId: currentPortfolioId },
    skip: !currentPortfolioId,
  });

  const locationCategories = React.useMemo(() => {
    const categories = new Map();

    const addValue = (name, position, value) => {
      if (!name || value <= 0) return;
      const existing = categories.get(name) || {
        name,
        value: 0,
        positionIds: new Set(),
      };

      existing.value += value;
      existing.positionIds.add(position.id);
      categories.set(name, existing);
    };

    (positions || []).forEach((position) => {
      const balances = Array.isArray(position?.locationBalances) ? position.locationBalances : [];
      if (balances.length === 0) return;

      const price = parseFloatFromString(position?.token?.marketData?.currentPrice || 0);
      const locatedQuantityStrings = balances.map((balance) => balance.quantity || '0');

      balances.forEach((balance) => {
        const value = parseFloatFromString(balance.quantity || 0) * price;
        addValue(balance.name || 'Unknown', position, value);
      });

      const unassignedQuantity = subtractDecimalStrings(position?.holdingsAmount || '0', locatedQuantityStrings);
      const unassignedValue = parseFloatFromString(unassignedQuantity) * price;
      if (unassignedValue > 0) {
        addValue('Unassigned', position, unassignedValue);
      }
    });

    const totalValue = Array.from(categories.values()).reduce((sum, category) => sum + category.value, 0);
    return Array.from(categories.values())
      .map((category) => ({
        name: category.name,
        value: String(category.value),
        percentage: totalValue > 0 ? String((category.value / totalValue) * 100) : '0',
        positionCount: category.positionIds.size,
      }))
      .sort((a, b) => parseFloatFromString(b.value) - parseFloatFromString(a.value));
  }, [positions]);

  if (!currentPortfolioId) {
    return <PortfolioRequired title="Welcome to Your Portfolio Dashboard" description="" />;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error.message}</span>
      </div>
    );
  }

  const summary = data?.portfolioSummary;
  const hasPositions = positions && positions.length > 0;
  const savedGoal = summary?.goal;

  // Check if user has recent transaction activity (for heatmap default)
  const hasRecentActivity = positions?.some((p) => {
    const txs = p?.transactions || [];
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return txs.some((tx) => new Date(tx.timestamp).getTime() > thirtyDaysAgo);
  });

  // Mobile order: Metrics, Planning, Charts, MC, Groups, Locations
  // Desktop: Two independent columns - Left (Metrics, Planning, MC), Right (Charts, Groups, Locations)
  // Using `contents` on mobile to flatten the structure for reordering
  return (
    <div className="px-2 sm:px-3 lg:px-4 py-2">
      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
      {/* Left Column - contents on mobile allows children to reorder */}
      <div className="contents lg:flex lg:flex-col lg:flex-1 lg:min-w-0 lg:gap-4">
        <div className="order-1 lg:order-none">
          <PortfolioMetrics
            portfolio={currentPortfolio}
            holdingsValue={summary?.holdingsValue}
            currentPnl={parseFloatFromString(summary?.current?.pnl || '0')}
            currentRoi={parseFloatFromString(summary?.current?.roi || '0')}
            valueChange24h={parseFloatFromString(summary?.valueChange24h || '0')}
            valueChangePercentage24h={parseFloatFromString(summary?.valueChangePercentage24h || '0')}
            // Altcoin stats (excl. BTC)
            altcoinHoldings={summary?.holdingsValueExcludingBTC}
            altcoinPnl={summary?.currentPnLExcludingBTC}
            altcoinRoi={summary?.currentROIExcludingBTC}
            altcoinChange24h={summary?.valueChange24hExcludingBTC}
            altcoinChangePercentage24h={summary?.valueChangePercentage24hExcludingBTC}
            // Altcoin stats (excl. BTC+ETH)
            altcoinExEthHoldings={summary?.holdingsValueExcludingBTCAndETH}
            altcoinExEthPnl={summary?.currentPnLExcludingBTCAndETH}
            altcoinExEthRoi={summary?.currentROIExcludingBTCAndETH}
            altcoinExEthChange24h={summary?.valueChange24hExcludingBTCAndETH}
            altcoinExEthChangePercentage24h={summary?.valueChangePercentage24hExcludingBTCAndETH}
            onOpenPortfolioManager={() => setShowPortfolioManager(true)}
          />
        </div>

        {hasPositions && (
          <div className="order-2 lg:order-none">
            <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <PortfolioBreakdownBar
                investedAmount={summary?.current?.basisCost}
                holdingsValue={summary?.holdingsValue}
                realisedAmount={summary?.current?.proceeds}
                currency={currentPortfolio?.currency}
              />
            </div>
          </div>
        )}

        {hasPositions && (
          <div className="order-3 lg:order-none">
            <PlanningStatus
              positions={positions}
              planned={summary?.planned}
              total={summary?.total}
              invalidationStats={summary?.invalidationStats}
            />
          </div>
        )}

        {hasPositions ? (
          <div className="order-4 lg:order-none">
            <MarketCapBreakdown
              marketCapCategories={summary?.marketCapCategories}
              goal={savedGoal}
              onOpenGoals={() => setIsGoalsOpen(true)}
            />
          </div>
        ) : (
          <div className="order-4 lg:order-none">
            <EmptyState
              title="No positions yet"
              description={
                <span className="text-sm">
                  Search tokens with <span className="font-mono bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded">Cmd+J</span> and add transactions one by one.<br />
                  Or batch import from CSV or Excel file.
                </span>
              }
              action={() => openImportModal({ portfolioId: currentPortfolioId })}
              actionLabel="Import Transactions"
              actionIcon={
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
              }
            />
          </div>
        )}
      </div>

      {/* Right Column - contents on mobile allows children to reorder */}
      <div className="contents lg:flex lg:flex-col lg:flex-1 lg:min-w-0 lg:gap-4">
        <div className="order-3 lg:order-none">
          <TabbedCharts hasRecentActivity={hasRecentActivity} />
        </div>

        {hasPositions && (
          <div className="order-5 lg:order-none">
            <GroupBreakdown groupCategories={summary?.groupCategories} />
          </div>
        )}

        {hasPositions && (
          <div className="order-6 lg:order-none">
            <LocationBreakdown locationCategories={locationCategories} />
          </div>
        )}
      </div>

      {/* Modals */}
      <InvestmentGoalsModal
        isOpen={isGoalsOpen}
        onClose={() => setIsGoalsOpen(false)}
        initialMultiple={1.5}
        initialGoal={savedGoal}
        onContinue={() => setIsGoalsOpen(false)}
      />

      {showPortfolioManager && (
        <PortfolioManagementModal
          isOpen={showPortfolioManager}
          onClose={() => setShowPortfolioManager(false)}
        />
      )}
      </div>
    </div>
  );
};

export default DashboardPage;
